import { api } from '@@/services/api';

export const documentApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getDocument: builder.query<Unpatient.Document, string>({
      query: (documentId) => `documents/${documentId}`,
      providesTags: ['Documents'],
    }),
    viewDocument: builder.query<string, string>({
      query: (documentId) => ({
        url: `documents/${documentId}/view`,
        responseHandler: async (response) => {
          const contentType = response.headers.get('Content-Type');
          const blob = await response.blob();

          return URL.createObjectURL(
            contentType ? new Blob([blob], { type: contentType }) : blob,
          );
        },
      }),
      // The cached value is an object URL, not raw bytes. Revoke it when
      // RTK Query evicts the entry so we don't leak blobs across the
      // session as users browse media.
      async onCacheEntryAdded(_arg, { cacheDataLoaded, cacheEntryRemoved }) {
        let url: string | undefined;
        try {
          const { data } = await cacheDataLoaded;
          url = data;
          await cacheEntryRemoved;
        } finally {
          if (url) {
            URL.revokeObjectURL(url);
          }
        }
      },
    }),
    getUserDocuments: builder.query<Unpatient.Document[], string>({
      query: (userId) => `users/${userId}/documents`,
      providesTags: ['Documents'],
    }),
    searchUserDocuments: builder.query<
      Unpatient.Document[],
      { userId: string; s: string }
    >({
      query: ({ userId, s }) => `users/${userId}/documents/search?s=${s}`,
      providesTags: ['Documents'],
    }),
    createUserDocuments: builder.mutation<
      Unpatient.Document[],
      { userId: string; files: File[] }
    >({
      query: ({ userId, files }) => {
        const formData = new FormData();

        for (const file of files) {
          formData.append('file', file);
        }

        return {
          url: `users/${userId}/documents`,
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ['Documents'],
    }),
    deleteDocument: builder.mutation<{}, string>({
      query: (documentId) => ({
        url: `documents/${documentId}`,
        method: 'DELETE',
        body: {},
      }),
      invalidatesTags: ['Documents'],
    }),
    patchDocument: builder.mutation<
      Unpatient.Document,
      {
        documentId: string;
        isPrivate?: boolean;
        label?: string;
        type?: string;
        documentCreationDate?: Date;
      }
    >({
      query: ({ documentId, ...body }) => {
        return {
          url: `documents/${documentId}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['Documents'],
    }),
    addScreeshotToUserDocuments: builder.mutation<
      void,
      { userId: string; url: string }
    >({
      query: ({ userId, url }) => {
        return {
          url: `users/${userId}/documents/screenshot`,
          method: 'POST',
          body: { url },
        };
      },
      invalidatesTags: ['Documents'],
    }),
  }),
});

export const {
  useGetDocumentQuery,
  useDeleteDocumentMutation,
  useCreateUserDocumentsMutation,
  useGetUserDocumentsQuery,
  useViewDocumentQuery,
  usePatchDocumentMutation,
  useSearchUserDocumentsQuery,
  useAddScreeshotToUserDocumentsMutation,
} = documentApi;
